import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerRecordDto,
  type BuyerRecordVersionDto,
  type BuyerSubmissionDetailDto,
} from "@/api/buyers";
import {
  Button,
  CategoryBadge,
  Skeleton,
  StateMark,
  SubTable,
  Surface,
  TableValue,
  Timestamp,
  type CellDoor,
} from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { DetailSection } from "../detailBits";
import { NdaAttestationModal, type NdaAttestationMode } from "../NdaAttestationModal";
import { RecordEntryThread } from "./RecordEntryThread";

/**
 * The NDA on file for this pairing — the current one, its notes, and everything
 * it superseded.
 *
 * ============================================================================
 * 🔴 THREE INDEPENDENT FACTS, NEVER ONE OUTCOME
 * ============================================================================
 *
 * <p><b>Signed</b>, <b>sent to the broker</b> and <b>none required</b> are three
 * separate columns on the record, and a version may carry all three at once.
 * That is not an accident of the schema — it is the rule the server states
 * outright: a submission that recorded "this broker does not use an NDA" and
 * then received one anyway keeps both facts, because the second does not make
 * the first untrue about the time it described.</p>
 *
 * <p>So they render as three ROWS with three states and three doors. Resolving
 * them into one status field — "NDA: signed" — would have to pick a winner, and
 * whichever it picked would delete a fact somebody recorded on purpose.</p>
 *
 * ============================================================================
 * 🔴 SUPERSEDED, NEVER OVERWRITTEN
 * ============================================================================
 *
 * <p>When the broker returns their own paper, recording it appends a new version
 * rather than editing the old one. The card shows the NDA in force and collapses
 * the rest behind "N earlier NDAs" — present, readable, with their own dates and
 * their own notes, and not in the way.</p>
 *
 * ============================================================================
 * 🔴 VERSION 1 MAY BE A MIGRATED RECORD, AND MUST SAY SO
 * ============================================================================
 *
 * <p>A version whose origin is `RecordedBeforeVersioning` was carried in by the
 * migration from the flat NDA columns. <b>Its dates are true. Its authorship is
 * not.</b> The old `NdaSignedByUserId` was assigned with `=` rather than `??=`
 * — deliberately, so a mis-keyed date could be corrected by re-recording — which
 * means it holds the LAST person to re-record, and nothing in the old schema
 * distinguished that from the original signer. No amount of reading the row
 * recovers it.</p>
 *
 * <p>This card therefore never prints "Signed by X" for such a version. It
 * prints the name under the only label that is true of it — <i>last recorded
 * by</i> — beside a marker saying what the record is.</p>
 */
export function NdaRecordCard({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  const [mode, setMode] = useState<NdaAttestationMode | null>(null);

  const record = useQuery({
    queryKey: buyerSubmissionKeys.record(s.id, "Nda"),
    queryFn: () => buyerSubmissionsApi.getRecord(s.id, "Nda"),
  });

  const current = record.data?.current ?? null;
  const earlier = (record.data?.versions ?? []).filter((v) => !v.isCurrent);

  // 🔴 The record's own answer, not the page's. `canWrite` is the module
  // permission plus the row's ownership; `record.canWrite` is the service's
  // verdict on this record. Both must hold, and the service's is the one that
  // decides a request.
  const mayWrite = canWrite && (record.data?.canWrite ?? false);

  return (
    <DetailSection
      title="NDA"
      action={
        // Only once there is something to supersede. Before that, the empty
        // rows carry their own doors and a second entry point would be a second
        // way to do the same thing.
        mayWrite && current?.signedAtUtc ? (
          <Button
            variant="text"
            size="sm"
            data-testid="nda-record-replacement"
            onClick={() => setMode("signed")}
          >
            Record a replacement
          </Button>
        ) : null
      }
    >
      {record.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="grid gap-space-4">
          <Surface kind="card">
            <SubTable
              caption="NDA facts"
              data-testid="nda-facts"
              rowKey={(r) => r.key}
              rowTestId={(r) => `nda-fact-${r.key}`}
              columns={[
                { key: "fact", header: "Fact", render: (r) => r.label },
                { key: "state", header: "State", render: (r) => r.state },
                { key: "detail", header: "Detail", render: (r) => r.detail },
              ]}
              rows={ndaFactRows(current, mayWrite, setMode)}
            />
          </Surface>

          {current && <PreVersioningNote version={current} />}

          {current ? (
            <div className="grid gap-space-2">
              <h3 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0">
                Authorisation notes
              </h3>
              <RecordEntryThread
                versionId={current.id}
                entries={current.entries}
                canWrite={mayWrite}
                data-testid="nda-entries"
              />
            </div>
          ) : (
            <p className="text-caption text-gray-mid m-0">
              Notes are recorded against an NDA. Record one of the facts above and
              this becomes a dated, attributed thread.
            </p>
          )}

          {earlier.length > 0 && (
            <EarlierVersions versions={earlier} canWrite={mayWrite} />
          )}
        </div>
      )}

      {/* At the TOP LEVEL of the section rather than inside the collapsed
          history: an overlay mounted inside closed collapsible content is the
          shape that once painted nothing while still locking the page's
          scroll. */}
      <NdaAttestationModal
        submissionId={s.id}
        open={mode !== null}
        mode={mode ?? "signed"}
        supersedes={mode === "signed" && !!current?.signedAtUtc}
        onClose={() => setMode(null)}
      />
    </DetailSection>
  );
}

interface NdaFactRow {
  key: string;
  label: string;
  state: React.ReactNode;
  detail: React.ReactNode;
}

/**
 * The three facts as rows.
 *
 * <p>Each absence is a WORKFLOW STATE somebody could act on, not a blank — which
 * is why every one of them carries a door rather than an em dash. Recording that
 * the NDA went to the broker is a thing a specialist does; the field being empty
 * is work, not a gap in the data.</p>
 *
 * <p>All three doors are `stamp`: they write the fact they name, through the one
 * writer that runs the prequalification gate and keeps the package gate's mirror
 * current.</p>
 */
function ndaFactRows(
  current: BuyerRecordVersionDto | null,
  mayWrite: boolean,
  open: (mode: NdaAttestationMode) => void,
): NdaFactRow[] {
  const door = (label: string, mode: NdaAttestationMode): CellDoor => ({
    kind: "stamp",
    label,
    onClick: () => open(mode),
    disabled: !mayWrite,
    // A reader gets the fact and no control. Nothing is disabled-but-visible:
    // an affordance the server will refuse cannot be told apart from a broken
    // one.
    disabledReason: "Not recorded",
  });

  const migrated = current?.origin === "RecordedBeforeVersioning";

  return [
    {
      key: "signed",
      label: "Signed",
      state: (
        <StateMark
          present={!!current?.signedAtUtc}
          presentLabel="Signed"
          absentLabel="Not signed"
        />
      ),
      detail: (
        <TableValue
          value={
            current?.signedAtUtc ? (
              <span className="grid gap-space-1">
                <Timestamp value={current.signedAtUtc} />
                {current.signedByUserName && (
                  <span className="text-caption text-gray-mid">
                    {/* 🔴 The label changes with the origin. On a migrated
                        version this name is the last person to re-record, which
                        is not the same claim as "signed by". */}
                    {migrated ? "Last recorded by" : "Recorded by"}{" "}
                    {current.signedByUserName}
                  </span>
                )}
              </span>
            ) : null
          }
          door={door("Record the signed NDA", "signed")}
        />
      ),
    },
    {
      key: "sent",
      label: "Sent to the broker",
      state: (
        <StateMark
          present={!!current?.sentAtUtc}
          presentLabel="Sent"
          absentLabel="Not sent"
        />
      ),
      detail: (
        <TableValue
          value={current?.sentAtUtc ? <Timestamp value={current.sentAtUtc} /> : null}
          door={door("Record when it went out", "sent")}
        />
      ),
    },
    {
      key: "not-required",
      label: "None required",
      state: (
        <StateMark
          present={!!current?.notRequiredReason}
          presentLabel="Recorded"
          // Deliberately not "Required": nobody asserted that either. The
          // absence means the question has not been answered.
          absentLabel="Not recorded"
        />
      ),
      detail: (
        <TableValue
          value={current?.notRequiredReason ?? null}
          door={door("Record that none was required", "not-required")}
        />
      ),
    },
  ];
}

/** The marker that keeps a migrated version from being read as an attribution. */
function PreVersioningNote({ version }: { version: BuyerRecordVersionDto }) {
  if (version.origin !== "RecordedBeforeVersioning") return null;
  return (
    <p
      className="text-caption text-gray-mid m-0 flex flex-wrap items-center gap-space-2"
      data-testid="nda-pre-versioning"
    >
      <CategoryBadge>Pre-versioning record</CategoryBadge>
      <span>
        Carried in from the NDA columns as they stood when versioning arrived. The
        dates are accurate; the name on file is the last person to record an NDA
        detail, which is not necessarily whoever signed it.
      </span>
    </p>
  );
}

/**
 * Everything this NDA superseded, collapsed.
 *
 * <p>Collapsed rather than absent: an earlier NDA is why the current one exists,
 * and a specialist asked "what did we have on file in March" needs it to be
 * somewhere. Collapsed rather than expanded: on all but a handful of submissions
 * there is nothing here, and on the rest it is history rather than work.</p>
 */
function EarlierVersions({
  versions,
  canWrite,
}: {
  versions: BuyerRecordVersionDto[];
  canWrite: boolean;
}) {
  return (
    <details className="border-t border-border-light pt-space-3" data-testid="nda-earlier">
      <summary className="cursor-pointer text-caption text-gray-mid">
        {versions.length === 1 ? "1 earlier NDA" : `${versions.length} earlier NDAs`}
      </summary>

      <ol className="grid gap-space-3 mt-space-3 list-none p-0 m-0">
        {versions.map((v) => (
          <li
            key={v.id}
            className="rounded-md border border-border-light p-space-3"
            data-testid="nda-earlier-version"
          >
            <div className="flex flex-wrap items-center gap-space-2">
              <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
                Version {v.versionNumber}
              </span>
              {v.origin === "RecordedBeforeVersioning" && (
                <CategoryBadge>Pre-versioning record</CategoryBadge>
              )}
              <span className="text-caption text-gray-mid">
                Recorded <Timestamp value={v.recordedAtUtc} />
              </span>
            </div>

            <dl className="grid gap-space-1 mt-space-2 m-0">
              <SupersededFact
                label="Signed"
                value={v.signedAtUtc ? <Timestamp value={v.signedAtUtc} /> : null}
              />
              <SupersededFact
                label="Sent to the broker"
                value={v.sentAtUtc ? <Timestamp value={v.sentAtUtc} /> : null}
              />
              <SupersededFact label="None required" value={v.notRequiredReason ?? null} />
            </dl>

            {/* No composer: a new note belongs on the NDA in force. An author
                may still correct their own last word here, which is the
                server's rule and not this card's. */}
            <div className="mt-space-2">
              <RecordEntryThread
                versionId={v.id}
                entries={v.entries}
                canWrite={canWrite}
                compose={false}
              />
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function SupersededFact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-space-2">
      <dt className="text-caption uppercase tracking-wide text-gray-mid">{label}</dt>
      {/* A superseded version is history — there is nothing to act on, so a
          missing fact here is a plain blank rather than a door. */}
      <dd className="text-small text-onyx m-0">
        <TableValue value={value} />
      </dd>
    </div>
  );
}

export type { BuyerRecordDto };
